import { useQuery } from '@tanstack/react-query';

import { useTRPC } from '#src/trpc.js';
import { captureAnalyticsEvent } from '#utils/analytics/analytics-events.js';
import {
	buildSchedulingEmbedUrl,
	DESIGN_MONTH_GATE_CONTACT_EMAIL,
	DESIGN_MONTH_GATE_SCHEDULING_URL,
} from './design-month-gate-contact.js';
import { DesignMonthGateDialog } from './design-month-gate-dialog.js';

import type { ReactElement } from 'react';

export interface CreditRequiredContactGateProps {
	open: boolean;
	/** The organization the design belongs to, by its URL name. */
	orgName: string;
	/** The revision waiting on credit. */
	revisionId: string;
	/** Called when the customer leaves without contacting Cadstrom. */
	onClose: () => void;
}

/**
 * The contact gate for a revision waiting on design review credit, offered to an
 * organization that cannot buy credit itself. Books a call with Cadstrom, with the
 * booking page filled in for the signed-in customer, or emails them.
 *
 * @param props - The gate's props.
 * @returns The gate dialog.
 */
export function CreditRequiredContactGate({
	open,
	orgName,
	revisionId,
	onClose,
}: CreditRequiredContactGateProps): ReactElement {
	const trpc = useTRPC();
	const { data: currentUser } = useQuery({ ...trpc.user.getCurrent.queryOptions(), throwOnError: false });
	const { data: orgData } = useQuery({
		...trpc.organization.get.queryOptions({ organizationName: orgName }),
		throwOnError: false,
	});
	const schedulingEmbedUrl =
		DESIGN_MONTH_GATE_SCHEDULING_URL === null ? null : (
			buildSchedulingEmbedUrl(DESIGN_MONTH_GATE_SCHEDULING_URL, window.location.host, {
				name: currentUser?.actor.displayName,
				email: currentUser?.actor.email,
			})
		);
	const event = { organization_id: orgData?.organization.id ?? null, revision_id: revisionId };

	return (
		<DesignMonthGateDialog
			open={open}
			closeLabel='Not now'
			onClose={onClose}
			contactEmail={DESIGN_MONTH_GATE_CONTACT_EMAIL}
			schedulingEmbedUrl={schedulingEmbedUrl}
			onShown={() => captureAnalyticsEvent('credit_required_contact_shown', event)}
			onSchedulerOpened={() => captureAnalyticsEvent('credit_required_contact_scheduler_opened', event)}
			onEmailChosen={() => captureAnalyticsEvent('credit_required_contact_email_chosen', event)}
		/>
	);
}
